import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../core/api/client';
import { t } from '../../platform/lang';

/**
 * Asking for a day off, or for a back-up, before the day arrives.
 *
 * The app already had a way to hand over a route — but it hung off a route that
 * exists, which means today only, and no later than three hours before pickup.
 * That is the emergency. This is the ordinary case: somebody knows about next
 * Tuesday now, and had no way to say so except a phone call that may or may not
 * be written down.
 *
 * The office answers in the dashboard, and the answer shows up here. Nothing is
 * approved automatically — a person decides who covers the work.
 */

interface Request {
  id: string;
  from_date: string;
  to_date: string;
  kind: string;
  reason: string;
  status: string;
  decision_note: string | null;
}

/** Today in the phone's own calendar, as the date input wants it. */
function todayISO(): string {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

const STATUS: Record<string, { label: string; className: string }> = {
  pending:   { label: 'Waiting for an answer', className: 'chip' },
  approved:  { label: 'Approved',              className: 'chip ok' },
  declined:  { label: 'Not approved',          className: 'chip warn' },
  cancelled: { label: 'Cancelled',             className: 'chip' },
};

export function CoverageScreen() {
  const navigate = useNavigate();
  const [from, setFrom] = useState(todayISO());
  const [to, setTo] = useState(todayISO());
  /*
   * Always 'backup'. The driver no longer chooses — see the note by the form.
   * Kept as a constant rather than inlined so the API contract stays visible.
   */
  const kind = 'backup' as const;
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  /*
   * Who the driver says will cover them. Two plain boxes, typed, never a list:
   * the owner's rule (29 Sep) — forty names in a dropdown on a phone confuses
   * drivers who are not comfortable with apps. Optional, because a driver who
   * needs help and has nobody lined up must still be able to ask.
   */
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  /* Route start time when the server says it is too late to ask (136). */
  const [tooLate, setTooLate] = useState<string | null>(null);
  const [mine, setMine] = useState<Request[]>([]);

  const load = useCallback(async () => {
    try {
      const { requests } = await api.listCoverageRequests();
      setMine(requests);
    } catch {
      // Offline. The form still works; the list simply is not shown.
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // A single-day request is the common one, so the end date follows the start
  // until the driver deliberately moves it.
  const pickFrom = (v: string) => {
    setFrom(v);
    if (to < v) setTo(v);
  };

  const send = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const r = await api.requestCoverage({
        from, to, kind, reasonCode, note: note.trim() || null,
        backupName: `${first.trim()} ${last.trim()}`.trim() || null,
      });
      if (r.ok) {
        setNotice(t('covSent'));
        setReasonCode('');
        setNote('');
        setFirst('');
        setLast('');
        await load();
      } else if (r.call_office) {
        // Inside 3 hours of the route: the office has to be called (136).
        setTooLate(r.route_start ?? '');
      } else {
        // The database writes these messages for the driver — "you already have
        // a request waiting for those days" — so they are shown as they are.
        setNotice(r.error ?? t('covFailed'));
      }
    } catch {
      setNotice(t('covOffline'));
    } finally {
      setBusy(false);
    }
  };

  /*
   * Named reasons, and "Something else" must be written out — the same rule as
   * skipping a stop. The database enforces it too (migration 045); this only
   * stops the driver being refused after filling the form in.
   */
  const REASONS: Array<{ code: string; label: string }> = [
    { code: 'personal',            label: t('covRPersonal') },
    { code: 'sick',                label: t('covRSick') },
    { code: 'medical_appointment', label: t('covRMedical') },
    { code: 'vacation',            label: t('covRVacation') },
    { code: 'vehicle_problem',     label: t('covRVehicle') },
    { code: 'family',              label: t('covRFamily') },
    { code: 'other',               label: t('covROther') },
  ];
  const otherOk = reasonCode !== 'other' || note.trim().length >= 4;
  const valid = reasonCode !== '' && otherOk && from !== '' && to >= from;

  return (
    <main className="screen">
      <button className="btn link" onClick={() => navigate('/route')}>← Route</button>
      <h1>{t('covTitle')}</h1>
      <p className="hint">{t('covIntro')}</p>

      {notice && <div className="banner">{notice}</div>}

      {tooLate !== null && (
        <div className="popup-backdrop" role="dialog" aria-modal="true" aria-labelledby="cov-late-title">
          <div className="popup">
            <h2 id="cov-late-title">{t('covTooLateTitle')}</h2>
            <p>{t('covTooLate', { time: tooLate })}</p>
            <button className="btn primary" onClick={() => navigate('/help')}>{t('covCallOffice')}</button>
            <button className="btn secondary" onClick={() => setTooLate(null)}>{t('covClose')}</button>
          </div>
        </div>
      )}

      <div className="col">
        {/*
          * There used to be a choice here: "time off" or "backup". It is gone.
          *
          * They were never two different things. A driver who is away needs
          * somebody to run their route — that is the whole request, and it is
          * one decision for the office: approve the cover, or do not. Asking
          * the driver to classify it first only created a way to get it wrong,
          * and two labels for one outcome on the approval screen.
          *
          * Every request is now recorded as 'backup', which is what actually
          * happens. The column still accepts the old value so requests made
          * before this change still read correctly.
          */}
        <label className="col">
          <span className="muted small">{t('covFrom')}</span>
          <input className="input" type="date" value={from} min={todayISO()}
                 onChange={(e) => pickFrom(e.target.value)} />
        </label>
        <label className="col">
          <span className="muted small">{t('covTo')}</span>
          <input className="input" type="date" value={to} min={from}
                 onChange={(e) => setTo(e.target.value)} />
        </label>
        <p className="hint">{t('covOneDay')}</p>

        <label className="col">
          <span className="muted small">{t('covReasonPick')}</span>
          <select className="input" value={reasonCode}
                  onChange={(e) => setReasonCode(e.target.value)}>
            <option value="">{t('covReasonPick')}…</option>
            {REASONS.map((r) => (
              <option key={r.code} value={r.code}>{r.label}</option>
            ))}
          </select>
        </label>

        {/* Only asked for when it is the only thing that carries meaning. */}
        {reasonCode === 'other' && (
          <label className="col">
            <span className="muted small">{t('covOtherWhat')}</span>
            <textarea className="input" rows={3} value={note}
                      onChange={(e) => setNote(e.target.value)} />
          </label>
        )}

        <div className="col">
          <span className="muted small">{t('covWho')}</span>
          <input className="input" type="text" autoComplete="off" autoCapitalize="words"
                 placeholder={t('covFirst')} value={first} maxLength={60}
                 onChange={(e) => setFirst(e.target.value)} />
          <input className="input" type="text" autoComplete="off" autoCapitalize="words"
                 placeholder={t('covLast')} value={last} maxLength={60}
                 onChange={(e) => setLast(e.target.value)} />
          <p className="hint">{t('covWhoHint')}</p>
        </div>

        <button className="btn primary big" disabled={!valid || busy} onClick={() => void send()}>
          {busy ? t('covSending') : t('covSend')}
        </button>
        {!valid && (
          <p className="hint">
            {reasonCode === 'other' && !otherOk ? t('covOtherWhat') : t('covNeedPick')}
          </p>
        )}
      </div>

      {mine.length > 0 && (
        <section style={{ marginTop: 24 }}>
          <h2>{t('covMine')}</h2>
          <ul className="plain-list">
            {mine.map((r) => (
              <li key={r.id} className="col" style={{ padding: '10px 0' }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <strong>
                    {r.from_date}{r.to_date !== r.from_date && <> → {r.to_date}</>}
                  </strong>
                  <span className={STATUS[r.status]?.className ?? 'chip'}>
                    {STATUS[r.status]?.label ?? r.status}
                  </span>
                </div>
                <span className="muted small">{r.reason}</span>
                {r.decision_note && <span className="small">{r.decision_note}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
